/**
 * Canonical list of API routes probed from the hosted SPA (after login) for CORS + reachability.
 * Paths are relative to getApiBaseUrl() (which already ends with /api/v1).
 * Keep in sync with backend/services/api-router/lambda_handler.py routing.
 */

export type ApiIntegrationManifestEntry = {
  /** Stable id for reports */
  id: string;
  /** Appended to API base (…/api/v1) */
  path: string;
  /** Run browser OPTIONS preflight (matches real SPA headers incl. cache-busting) */
  options: boolean;
  /** Run GET (omit when route is POST-only or expensive) */
  get: boolean;
  /** Attach Authorization from localStorage for GET */
  authForGet: boolean;
  /** Valid GET status codes (2xx/4xx still mean CORS + routing worked) */
  getOkStatuses: readonly number[];
};

const CC = [
  "cover-letter",
  "email-response",
  "follow-up",
  "interview-confirmation",
  "legal-review",
  "message-review",
  "offer-response",
  "recruiter-outreach",
  "salary-negotiation",
  "stored",
  "thank-you-note",
  "writing-assistance",
] as const;

const coachOptionsOnly: ApiIntegrationManifestEntry[] = CC.map((slug) => ({
  id: `communication-coach-${slug}-options`,
  path: `/communication-coach/${slug}`,
  options: true,
  get: false,
  authForGet: false,
  getOkStatuses: [200],
}));

export const API_INTEGRATION_MANIFEST: readonly ApiIntegrationManifestEntry[] =
  [
    {
      id: "health-system",
      path: "/health/system",
      options: true,
      get: true,
      authForGet: false,
      getOkStatuses: [200],
    },
    {
      id: "auth-verify-token",
      path: "/auth/verify-token",
      options: true,
      get: true,
      authForGet: true,
      getOkStatuses: [200],
    },
    {
      id: "profiles-me",
      path: "/profiles/me",
      options: true,
      get: true,
      authForGet: true,
      getOkStatuses: [200],
    },
    {
      id: "resumes-list",
      path: "/resumes",
      options: true,
      get: true,
      authForGet: true,
      getOkStatuses: [200],
    },
    {
      id: "resumes-types",
      path: "/resumes/types",
      options: true,
      get: true,
      authForGet: true,
      getOkStatuses: [200],
    },
    {
      id: "job-applications-list",
      path: "/job-applications",
      options: true,
      get: true,
      authForGet: true,
      getOkStatuses: [200],
    },
    {
      id: "service-plans",
      path: "/service-plans",
      options: true,
      get: true,
      authForGet: true,
      getOkStatuses: [200],
    },
    {
      id: "service-plans-user-subscription",
      path: "/service-plans/user/subscription",
      options: true,
      get: true,
      authForGet: true,
      getOkStatuses: [200],
    },
    {
      id: "service-plans-user-usage",
      path: "/service-plans/user/usage",
      options: true,
      get: true,
      authForGet: true,
      getOkStatuses: [200],
    },
    {
      id: "job-search-agents",
      path: "/job-search-agents",
      options: true,
      get: true,
      authForGet: true,
      getOkStatuses: [200],
    },
    {
      id: "openai-health",
      path: "/openai/health",
      options: true,
      get: true,
      authForGet: true,
      getOkStatuses: [200],
    },
    {
      id: "tailored-resume-options",
      path: "/tailored-resume",
      options: true,
      get: false,
      authForGet: false,
      getOkStatuses: [200],
    },
    {
      id: "company-intel-options",
      path: "/company-intel",
      options: true,
      get: false,
      authForGet: false,
      getOkStatuses: [200],
    },
    {
      id: "interview-prep-options",
      path: "/interview-prep",
      options: true,
      get: false,
      authForGet: false,
      getOkStatuses: [200],
    },
    {
      id: "intelligent-search-options",
      path: "/intelligent-search",
      options: true,
      get: false,
      authForGet: false,
      getOkStatuses: [200],
    },
    {
      id: "support-requests-options",
      path: "/support-requests",
      options: true,
      get: false,
      authForGet: false,
      getOkStatuses: [200],
    },
    ...coachOptionsOnly,
  ];

export type ApiIntegrationRowResult = {
  id: string;
  path: string;
  optionsAttempted: boolean;
  optionsOk: boolean;
  optionsStatus?: number;
  optionsError?: string;
  getAttempted: boolean;
  getOk: boolean;
  getStatus?: number;
  getError?: string;
};

export type ApiIntegrationReport = {
  version: 1;
  startedAt: string;
  finishedAt: string;
  finished: boolean;
  origin: string;
  apiBase: string;
  rows: ApiIntegrationRowResult[];
  corsConsoleErrors: string[];
  failedCount: number;
};

declare global {
  interface Window {
    __KAIROS_API_INTEGRATION_RESULTS__?: ApiIntegrationReport;
  }
}
